import { useEffect, useState } from 'react';
import axios from 'axios';
import Spinner from '../../../components/Spinner';

const SEZIONI = [
  { key: 'cifra_affari', label: 'Cifra affari' },
  { key: 'ammortamenti', label: 'Ammortamenti' },
  { key: 'costo_personale', label: 'Costo personale' },
];

const COLONNE = {
  cifra_affari: ['anno', 'affari_totale', 'affari_in_lavori', 'affari_in_lavori_ati', 'reddito_netto', 'liquidita', 'capitale_netto', 'patrimonio'],
  ammortamenti: ['anno_pos', 'amm_beni_diversi', 'amm_attrezzi', 'noleggio', 'noleggio_stabile', 'leasing', 'totale_ammortamenti'],
  costo_personale: ['anno_pos', 'costo_operai', 'numero_operai', 'costo_tecnici', 'numero_tecnici', 'costo_amministrativi', 'numero_amministrativi', 'costo_dirigenti', 'numero_dirigenti', 'titolare_soci', 'quota_inail', 'versamenti_inps', 'versamenti_inail', 'versamenti_cepima'],
};

const ENDPOINT = (sezione, impresaId) => ({
  cifra_affari: `/api/dati-economici/cifra-affari/${impresaId}`,
  ammortamenti: `/api/dati-economici/ammortamenti/${impresaId}`,
  costo_personale: `/api/dati-economici/costo-personale/${impresaId}`,
}[sezione]);

const COLONNE_CALCOLATE = new Set(['totale_ammortamenti']);

const LABEL_MAP = {
  anno_pos: 'Anno',
  costo_operai: 'Operai €',
  numero_operai: 'N. operai',
  costo_tecnici: 'Tecnici €',
  numero_tecnici: 'N. tecnici',
  costo_amministrativi: 'Amm. €',
  numero_amministrativi: 'N. amm.',
  costo_dirigenti: 'Dirigenti €',
  numero_dirigenti: 'N. dir.',
  titolare_soci: 'Titolare/Soci €',
  quota_inail: 'Quote INAIL (0=no, 1=ditt.ind., N=soci)',
  versamenti_inps: 'INPS €',
  versamenti_inail: 'INAIL €',
  versamenti_cepima: 'CEPIMA €',
  amm_beni_diversi: 'Beni diversi €',
  amm_attrezzi: 'Attrezzi €',
  noleggio: 'Noleggio €',
  noleggio_stabile: 'Noleggio stabile €',
  leasing: 'Leasing €',
  totale_ammortamenti: 'Totale amm. €',
};
const fmt2 = (n) => n != null && n !== '' ? Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

function CellaModificabile({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  return editing ? (
    <input
      type="number"
      step="any"
      autoFocus
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      onBlur={() => setEditing(false)}
      className="w-24 border border-[#2E6096] rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#2E6096]"
    />
  ) : (
    <span
      onClick={() => setEditing(true)}
      className="w-24 inline-block px-1 py-0.5 text-xs text-gray-700 border border-gray-200 rounded cursor-pointer hover:border-[#2E6096] hover:bg-blue-50"
    >
      {fmt2(value)}
    </span>
  );
}

function TabellaSezione({ data, colonne, onChange, onElimina }) {
  if (data.length === 0) return (
    <p className="text-center text-gray-400 py-6 text-sm">Nessun dato — verrà importato da Access.</p>
  );
  return (
    <div className="overflow-x-auto">
      <table className="text-xs w-full">
        <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
          <tr>
            {colonne.map(c => (
              <th key={c} className="px-3 py-2 text-left whitespace-nowrap">
                {LABEL_MAP[c] || c.replace(/_/g, ' ')}
                {COLONNE_CALCOLATE.has(c) && <span className="ml-1 text-[10px] font-normal text-blue-400">(calc.)</span>}
              </th>
            ))}
            <th className="px-2 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {colonne.map(col => (
                <td key={col} className="px-2 py-1">
                  {COLONNE_CALCOLATE.has(col) ? (
                    <span className="w-24 inline-block px-1 py-0.5 text-xs text-gray-700 bg-gray-100 rounded border border-gray-200">
                      {fmt2(row[col])}
                    </span>
                  ) : typeof row[col] === 'number' || row[col] == null ? (
                    <CellaModificabile value={row[col]} onChange={v => onChange(i, col, v)} />
                  ) : (
                    <input
                      type="text"
                      value={row[col] ?? ''}
                      onChange={e => onChange(i, col, e.target.value)}
                      className="w-24 border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#2E6096]"
                    />
                  )}
                </td>
              ))}
              <td className="px-2 py-1">
                <button onClick={() => onElimina(i)} className="text-red-400 hover:text-red-600 text-xs font-medium px-1">✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TabDatiEconomici({ impresaId, impresa: impresaInit, onImpresaAggiornata }) {
  const [sezione, setSezione] = useState('cifra_affari');
  const [data, setData] = useState([]);
  const [dataCedente, setDataCedente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [incrementoIso, setIncrementoIso] = useState(!!impresaInit?.incremento_iso);
  const [savingIso, setSavingIso] = useState(false);
  const [mostraCedente, setMostraCedente] = useState(false);
  const [modalImporta, setModalImporta] = useState(false);
  const [pratichePrecedenti, setPratichePrecedenti] = useState([]);
  const [praticaSelezionata, setPraticaSelezionata] = useState(null);
  const [righeDisponibili, setRigheDisponibili] = useState([]);
  const [righeSelezionate, setRigheSelezionate] = useState(new Set());
  const [loadingImporta, setLoadingImporta] = useState(false);
  const [modalCedente, setModalCedente] = useState(false);
  const [tutteImprese, setTutteImprese] = useState([]);
  const [ricercaCedente, setRicercaCedente] = useState('');
  const [nomeCedenteSel, setNomeCedenteSel] = useState('');
  const [icpForm, setIcpForm] = useState({
    patrimonio_netto: impresaInit?.patrimonio_netto ?? '',
    liquidita_ok: !!impresaInit?.liquidita_ok,
    reddito_anno1: !!impresaInit?.reddito_anno1,
    reddito_anno2: !!impresaInit?.reddito_anno2,
    reddito_anno3: !!impresaInit?.reddito_anno3,
  });
  const [savingIcp, setSavingIcp] = useState(false);
  const [msgIcp, setMsgIcp] = useState('');

  function ricalcolaTotaleAmm(row) {
    if (!row || row.amm_beni_diversi == null && row.amm_attrezzi == null) return row;
    return { ...row, totale_ammortamenti: (row.amm_beni_diversi || 0) + (row.amm_attrezzi || 0) };
  }

  // Ordine cronologico: dall'anno più lontano (o posizione più bassa) al più recente
  function ordinaCronologico(arr) {
    return [...arr].sort((a, b) => sezione === 'cifra_affari'
      ? (a.anno || 0) - (b.anno || 0)
      : (a.anno_pos || 0) - (b.anno_pos || 0));
  }

  function carica() {
    setLoading(true);
    setMsg('');
    axios.get(ENDPOINT(sezione, impresaId))
      .then(r => {
        const tutti = sezione === 'ammortamenti' ? r.data.map(ricalcolaTotaleAmm) : r.data;
        const isCed = row => row.flag_cedente || (row.cedente && row.cedente.toLowerCase() !== 'nessuna');
        setData(ordinaCronologico(tutti.filter(row => !isCed(row))));
        setDataCedente(ordinaCronologico(tutti.filter(isCed)));
      })
      .catch(err => {
        setMsg('Errore nel caricamento: ' + (err.response?.data?.error || err.message));
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => { carica(); }, [sezione, impresaId]);

  function handleChange(idx, field, value, isCedente = false) {
    const setter = isCedente ? setDataCedente : setData;
    const parsed = value === '' ? null : (!isNaN(Number(value)) ? Number(value) : value);
    setter(prev => prev.map((row, i) => {
      if (i !== idx) return row;
      const updated = { ...row, [field]: parsed };
      if (sezione === 'ammortamenti' && (field === 'amm_beni_diversi' || field === 'amm_attrezzi')) {
        updated.totale_ammortamenti = (updated.amm_beni_diversi || 0) + (updated.amm_attrezzi || 0);
      }
      return updated;
    }));
  }

  function rigaVuota(isCedente = false) {
    if (sezione === 'cifra_affari') {
      return { anno: new Date().getFullYear(), affari_totale: null, affari_in_lavori: null, flag_cedente: isCedente };
    }
    const righe = isCedente ? dataCedente : data;
    const maxPos = righe.reduce((m, r) => Math.max(m, r.anno_pos || 0), 0);
    if (sezione === 'ammortamenti') {
      return { anno_pos: maxPos + 1, amm_beni_diversi: null, amm_attrezzi: null, noleggio: null, noleggio_stabile: null, leasing: null, totale_ammortamenti: 0 };
    }
    return { anno_pos: maxPos + 1, costo_operai: null, numero_operai: null, costo_tecnici: null, numero_tecnici: null, costo_amministrativi: null, numero_amministrativi: null, costo_dirigenti: null, numero_dirigenti: null, titolare_soci: null, quota_inail: 0, versamenti_inps: null, versamenti_inail: null, versamenti_cepima: null };
  }

  function aggiungiRiga(isCedente = false) {
    const setter = isCedente ? setDataCedente : setData;
    setter(prev => [...prev, rigaVuota(isCedente)]);
    if (isCedente) setMostraCedente(true);
  }

  function eliminaRiga(idx, isCedente = false) {
    const setter = isCedente ? setDataCedente : setData;
    setter(prev => prev.filter((_, i) => i !== idx));
  }

  async function apriModalImporta() {
    setModalImporta(true);
    setPraticaSelezionata(null);
    setRigheDisponibili([]);
    setRigheSelezionate(new Set());
    const r = await axios.get(`/api/dati-economici/pratiche-correlate/${impresaId}`);
    setPratichePrecedenti(r.data);
  }

  async function selezionaPratica(id) {
    setPraticaSelezionata(id);
    setLoadingImporta(true);
    try {
      const r = await axios.get(ENDPOINT(sezione, id));
      const righe = ordinaCronologico(sezione === 'ammortamenti' ? r.data.map(ricalcolaTotaleAmm) : r.data);
      setRigheDisponibili(righe);
      setRigheSelezionate(new Set(righe.map((_, i) => i)));
    } finally {
      setLoadingImporta(false);
    }
  }

  function toggleRiga(idx) {
    setRigheSelezionate(prev => {
      const s = new Set(prev);
      s.has(idx) ? s.delete(idx) : s.add(idx);
      return s;
    });
  }

  function confermaImporta() {
    const righe = righeDisponibili
      .filter((_, i) => righeSelezionate.has(i))
      .map(({ id: _id, impresa_id: _imp, ...rest }) => rest);
    setData(prev => {
      if (sezione === 'cifra_affari') {
        const anniEsistenti = new Set(prev.map(r => r.anno));
        const nuove = righe.filter(r => !anniEsistenti.has(r.anno));
        return [...prev, ...nuove].sort((a, b) => (a.anno || 0) - (b.anno || 0));
      }
      const posEsistenti = new Set(prev.map(r => r.anno_pos));
      const maxPos = prev.reduce((m, r) => Math.max(m, r.anno_pos || 0), 0);
      let offset = maxPos;
      const nuove = righe
        .filter(r => !posEsistenti.has(r.anno_pos))
        .map(r => ({ ...r, anno_pos: r.anno_pos }));
      const conflitto = righe.filter(r => posEsistenti.has(r.anno_pos));
      const riagganciate = conflitto.map(r => ({ ...r, anno_pos: ++offset }));
      return ordinaCronologico([...prev, ...nuove, ...riagganciate]);
    });
    setModalImporta(false);
  }

  // ── Importazione da impresa cedente (azienda diversa, già a sistema) ──────
  async function apriModalCedente() {
    setModalCedente(true);
    setRicercaCedente('');
    setNomeCedenteSel('');
    setRigheDisponibili([]);
    setRigheSelezionate(new Set());
    if (tutteImprese.length === 0) {
      const r = await axios.get('/api/imprese');
      setTutteImprese(r.data);
    }
  }

  async function selezionaImpresaCedente(imp) {
    setNomeCedenteSel(imp.ragione_sociale);
    setLoadingImporta(true);
    try {
      const r = await axios.get(ENDPOINT(sezione, imp.id));
      // Solo le righe "principali" della sorgente: non importiamo cedenti di cedenti
      const isCedSorgente = row => sezione === 'cifra_affari'
        ? row.flag_cedente
        : (row.cedente && row.cedente.toLowerCase() !== 'nessuna');
      const soloPrincipali = r.data.filter(row => !isCedSorgente(row));
      const righe = ordinaCronologico(sezione === 'ammortamenti' ? soloPrincipali.map(ricalcolaTotaleAmm) : soloPrincipali);
      setRigheDisponibili(righe);
      setRigheSelezionate(new Set(righe.map((_, i) => i)));
    } finally {
      setLoadingImporta(false);
    }
  }

  function confermaImportaCedente() {
    const righe = righeDisponibili
      .filter((_, i) => righeSelezionate.has(i))
      .map(({ id: _id, impresa_id: _imp, flag_cedente: _fc, cedente: _c, impresa_cedente: _ic, ...rest }) => rest);
    setDataCedente(prev => {
      if (sezione === 'cifra_affari') {
        const taggate = righe.map(r => ({ ...r, flag_cedente: true, impresa_cedente: nomeCedenteSel }));
        const anniEsistenti = new Set(prev.filter(r => (r.impresa_cedente || '') === nomeCedenteSel).map(r => r.anno));
        const nuove = taggate.filter(r => !anniEsistenti.has(r.anno));
        return ordinaCronologico([...prev, ...nuove]);
      }
      const taggate = righe.map(r => ({ ...r, cedente: nomeCedenteSel }));
      const righeStessoCedente = prev.filter(r => r.cedente === nomeCedenteSel);
      const posEsistenti = new Set(righeStessoCedente.map(r => r.anno_pos));
      let offset = righeStessoCedente.reduce((m, r) => Math.max(m, r.anno_pos || 0), 0);
      const senzaConflitto = taggate.filter(r => !posEsistenti.has(r.anno_pos));
      const conConflitto = taggate.filter(r => posEsistenti.has(r.anno_pos)).map(r => ({ ...r, anno_pos: ++offset }));
      return ordinaCronologico([...prev, ...senzaConflitto, ...conConflitto]);
    });
    setMostraCedente(true);
    setModalCedente(false);
  }

  function aggiungiRigaCedente(nomeCedente) {
    if (sezione === 'cifra_affari') {
      setDataCedente(prev => [...prev, { anno: new Date().getFullYear(), affari_totale: null, affari_in_lavori: null, flag_cedente: true, impresa_cedente: nomeCedente }]);
      return;
    }
    const righeStessoCedente = dataCedente.filter(r => r.cedente === nomeCedente);
    const maxPos = righeStessoCedente.reduce((m, r) => Math.max(m, r.anno_pos || 0), 0);
    const base = sezione === 'ammortamenti'
      ? { amm_beni_diversi: null, amm_attrezzi: null, noleggio: null, noleggio_stabile: null, leasing: null, totale_ammortamenti: 0 }
      : { costo_operai: null, numero_operai: null, costo_tecnici: null, numero_tecnici: null, costo_amministrativi: null, numero_amministrativi: null, costo_dirigenti: null, numero_dirigenti: null, titolare_soci: null, quota_inail: 0, versamenti_inps: null, versamenti_inail: null, versamenti_cepima: null };
    setDataCedente(prev => [...prev, { ...base, anno_pos: maxPos + 1, cedente: nomeCedente }]);
  }

  const risultatiCedente = ricercaCedente.trim().length >= 2
    ? tutteImprese.filter(i => i.id !== parseInt(impresaId) && i.ragione_sociale?.toLowerCase().includes(ricercaCedente.trim().toLowerCase())).slice(0, 30)
    : [];

  function labelPratica(p) {
    return [p.richiesta, p.numero_richiesta].filter(Boolean).join(' ') || `ID ${p.id}`;
  }

  function labelAnno(row) {
    if (sezione === 'cifra_affari') return row.anno || '—';
    return `Pos. ${row.anno_pos}`;
  }

  async function salva() {
    setSaving(true);
    setMsg('');
    try {
      // cifra_affari usa flag_cedente (boolean); ammortamenti/costo_personale usano cedente (string con nome azienda)
      let tutteLeRighe;
      if (sezione === 'cifra_affari') {
        tutteLeRighe = [
          ...data.map(r => ({ ...r, flag_cedente: false })),
          ...dataCedente.map(r => ({ ...r, flag_cedente: true })),
        ];
      } else {
        tutteLeRighe = [
          ...data.map(r => ({ ...r, cedente: null })),
          ...dataCedente, // il campo cedente contiene già il nome dell'azienda cedente
        ];
      }
      await axios.put(ENDPOINT(sezione, impresaId), tutteLeRighe);
      setMsg('Salvato!');
      carica();
    } catch {
      setMsg('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }

  async function salvaIcp() {
    setSavingIcp(true);
    setMsgIcp('');
    try {
      const payload = {
        ...icpForm,
        patrimonio_netto: icpForm.patrimonio_netto !== '' ? parseFloat(icpForm.patrimonio_netto) : null,
      };
      await axios.put(`/api/imprese/${impresaId}`, payload);
      setMsgIcp('Salvato!');
      onImpresaAggiornata?.();
    } catch (err) {
      setMsgIcp('Errore: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingIcp(false);
    }
  }

  async function toggleIso() {
    setSavingIso(true);
    const nuovoValore = !incrementoIso;
    try {
      await axios.put(`/api/imprese/${impresaId}`, { incremento_iso: nuovoValore });
      setIncrementoIso(nuovoValore);
      onImpresaAggiornata?.();
    } finally {
      setSavingIso(false);
    }
  }

  const colonne = COLONNE[sezione];

  // Raggruppa cedenti per nome azienda
  const cedNomeField = sezione === 'cifra_affari' ? 'impresa_cedente' : 'cedente';
  const gruppiCedenti = dataCedente.reduce((acc, row, idx) => {
    const nome = row[cedNomeField] || 'Cedente';
    if (!acc[nome]) acc[nome] = [];
    acc[nome].push({ row, idx });
    return acc;
  }, {});

  return (
    <>
    <div className="space-y-4">
      {/* ISO 9001 toggle */}
      <div className="bg-white rounded-xl shadow px-5 py-4 flex items-center justify-between">
        <div>
          <p className="font-semibold text-[#1A3A5C] text-sm">Certificazione ISO 9001</p>
          <p className="text-xs text-gray-500 mt-0.5">Abilita il calcolo dell'ICP (Incremento Convenzionale Premiante)</p>
        </div>
        <button
          onClick={toggleIso}
          disabled={savingIso}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${incrementoIso ? 'bg-[#1A3A5C]' : 'bg-gray-300'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${incrementoIso ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Parametri ICP manuali */}
      <div className="bg-white rounded-xl shadow px-5 py-4">
        <p className="font-semibold text-[#1A3A5C] text-sm mb-1">Parametri ICP (inserimento manuale)</p>
        <p className="text-xs text-gray-500 mb-4">Patrimonio netto, liquidità e reddito vengono inseriti dall'operatore sulla base dei bilanci aziendali.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Patrimonio netto (€)</label>
            <input
              type="number"
              step="any"
              value={icpForm.patrimonio_netto}
              onChange={e => setIcpForm(f => ({ ...f, patrimonio_netto: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
              placeholder="es. 250000"
            />
          </div>
          <div className="flex flex-col justify-end">
            <label className="block text-xs font-medium text-gray-600 mb-2">Liquidità (indice ≥ 0,5)</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIcpForm(f => ({ ...f, liquidita_ok: !f.liquidita_ok }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${icpForm.liquidita_ok ? 'bg-[#1A3A5C]' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${icpForm.liquidita_ok ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-gray-600">{icpForm.liquidita_ok ? 'Verificata' : 'Non verificata'}</span>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-2">Reddito netto (ultimi 3 anni)</label>
          <div className="flex flex-wrap gap-4">
            {['reddito_anno1', 'reddito_anno2', 'reddito_anno3'].map((campo, i) => (
              <div key={campo} className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Anno {i + 1}:</span>
                <button
                  type="button"
                  onClick={() => setIcpForm(f => ({ ...f, [campo]: !f[campo] }))}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition ${icpForm[campo] ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}
                >
                  {icpForm[campo] ? '+ Positivo' : '− Negativo'}
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={salvaIcp}
            disabled={savingIcp}
            className="bg-[#1A3A5C] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition disabled:opacity-50"
          >
            {savingIcp ? 'Salvataggio...' : 'Salva parametri ICP'}
          </button>
          {msgIcp && <span className={`text-sm ${msgIcp === 'Salvato!' ? 'text-green-600' : 'text-red-600'}`}>{msgIcp}</span>}
        </div>
      </div>

      {/* Selettore sezione */}
      <div className="flex gap-2">
        {SEZIONI.map(s => (
          <button
            key={s.key}
            onClick={() => setSezione(s.key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${sezione === s.key ? 'bg-[#1A3A5C] text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {msg && msg.startsWith('Errore') && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{msg}</div>
      )}

      {loading ? <Spinner /> : (
        <>
          {/* Dati impresa principale */}
          <div className="bg-white rounded-xl shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-[#1A3A5C] text-sm">Impresa principale</p>
              <div className="flex items-center gap-3">
                <button
                  onClick={apriModalImporta}
                  className="text-xs text-gray-500 hover:text-[#2E6096] hover:underline"
                >
                  ↩ Importa da pratica precedente
                </button>
                <button
                  onClick={() => aggiungiRiga(false)}
                  className="text-xs text-[#2E6096] hover:underline"
                >
                  + Aggiungi riga
                </button>
              </div>
            </div>
            <TabellaSezione data={data} colonne={colonne} onChange={(i, f, v) => handleChange(i, f, v, false)} onElimina={i => eliminaRiga(i, false)} />
          </div>

          {/* Sezioni cedenti — una per ogni azienda */}
          {Object.entries(gruppiCedenti).map(([nome, righe]) => (
            <div key={nome} className="bg-white rounded-xl shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-[#1A3A5C] text-sm">
                  Cedente: <span className="text-[#2E6096]">{nome}</span>
                  <span className="ml-2 text-xs font-normal text-gray-500">(valori sommati nel calcolo)</span>
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => aggiungiRigaCedente(nome)}
                    className="text-xs text-[#2E6096] hover:underline"
                  >
                    + Aggiungi riga
                  </button>
                  <button
                    onClick={() => setMostraCedente(v => !v)}
                    className="text-xs text-gray-500 hover:underline"
                  >
                    {mostraCedente ? 'Nascondi' : `Mostra (${righe.length} righe)`}
                  </button>
                </div>
              </div>
              {mostraCedente && (
                <TabellaSezione
                  data={righe.map(r => r.row)}
                  colonne={colonne}
                  onChange={(i, f, v) => handleChange(righe[i].idx, f, v, true)}
                  onElimina={i => eliminaRiga(righe[i].idx, true)}
                />
              )}
            </div>
          ))}
          <div className="bg-white rounded-xl shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-[#1A3A5C] text-sm">Imprese cedenti</p>
                {Object.keys(gruppiCedenti).length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">Nessuna impresa cedente collegata a questa iscrizione.</p>
                )}
              </div>
              <button
                onClick={apriModalCedente}
                className="text-xs text-[#2E6096] hover:underline whitespace-nowrap"
              >
                + Aggiungi cedente (da scheda esistente)
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={salva}
              disabled={saving}
              className="bg-[#1A3A5C] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition disabled:opacity-50"
            >
              {saving ? 'Salvataggio...' : 'Salva modifiche'}
            </button>
            {msg && <span className={`text-sm ${msg === 'Salvato!' ? 'text-green-600' : 'text-red-600'}`}>{msg}</span>}
          </div>
        </>
      )}
    </div>

    {/* Modal importa da pratica precedente */}
    {modalImporta && (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-[#1A3A5C]">Importa dati — {SEZIONI.find(s => s.key === sezione)?.label}</h3>
            <button onClick={() => setModalImporta(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
          </div>

          <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
            {/* Selezione pratica */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Da quale pratica?</label>
              {pratichePrecedenti.length === 0 ? (
                <p className="text-sm text-gray-400">Nessuna altra pratica trovata per questa impresa.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {pratichePrecedenti.map(p => (
                    <button
                      key={p.id}
                      onClick={() => selezionaPratica(p.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${praticaSelezionata === p.id ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#2E6096]'}`}
                    >
                      {labelPratica(p)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selezione righe */}
            {praticaSelezionata && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Seleziona righe da importare</label>
                  <div className="flex gap-3">
                    <button onClick={() => setRigheSelezionate(new Set(righeDisponibili.map((_, i) => i)))} className="text-xs text-[#2E6096] hover:underline">Tutte</button>
                    <button onClick={() => setRigheSelezionate(new Set())} className="text-xs text-gray-400 hover:underline">Nessuna</button>
                  </div>
                </div>
                {loadingImporta ? (
                  <p className="text-sm text-gray-400 py-4 text-center">Caricamento...</p>
                ) : righeDisponibili.length === 0 ? (
                  <p className="text-sm text-gray-400">Nessun dato in questa pratica per la sezione corrente.</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                    {righeDisponibili.map((row, i) => (
                      <label key={i} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={righeSelezionate.has(i)}
                          onChange={() => toggleRiga(i)}
                          className="w-4 h-4 accent-[#1A3A5C]"
                        />
                        <span className="text-sm text-gray-700 font-medium w-20">{labelAnno(row)}</span>
                        {sezione === 'cifra_affari' && (
                          <span className="text-xs text-gray-400">
                            Affari: {fmt2(row.affari_totale)} · In lavori: {fmt2(row.affari_in_lavori)}
                          </span>
                        )}
                        {sezione === 'ammortamenti' && (
                          <span className="text-xs text-gray-400">
                            Beni diversi: {fmt2(row.amm_beni_diversi)} · Attrezzi: {fmt2(row.amm_attrezzi)}
                          </span>
                        )}
                        {sezione === 'costo_personale' && (
                          <span className="text-xs text-gray-400">
                            Operai: {fmt2(row.costo_operai)} · Tecnici: {fmt2(row.costo_tecnici)}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Le righe importate vengono aggiunte a quelle esistenti. Se un anno è già presente non viene duplicato.
                </p>
              </div>
            )}
          </div>

          <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-3">
            <button onClick={() => setModalImporta(false)} className="border border-gray-300 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Annulla</button>
            <button
              onClick={confermaImporta}
              disabled={!praticaSelezionata || righeSelezionate.size === 0}
              className="bg-[#1A3A5C] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition disabled:opacity-40"
            >
              Importa {righeSelezionate.size > 0 ? `(${righeSelezionate.size})` : ''}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Modal aggiungi cedente da scheda esistente */}
    {modalCedente && (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-[#1A3A5C]">Aggiungi cedente — {SEZIONI.find(s => s.key === sezione)?.label}</h3>
            <button onClick={() => setModalCedente(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
          </div>

          <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
            {/* Ricerca impresa cedente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cerca l'impresa cedente per ragione sociale</label>
              <input
                type="text"
                value={ricercaCedente}
                onChange={e => setRicercaCedente(e.target.value)}
                placeholder="Digita almeno 2 caratteri..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
              />
              {ricercaCedente.trim().length >= 2 && (
                <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                  {risultatiCedente.length === 0 ? (
                    <p className="text-sm text-gray-400 px-4 py-3">Nessuna impresa trovata.</p>
                  ) : risultatiCedente.map(imp => (
                    <button
                      key={imp.id}
                      onClick={() => selezionaImpresaCedente(imp)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 transition ${nomeCedenteSel === imp.ragione_sociale ? 'bg-blue-50 font-medium text-[#1A3A5C]' : 'text-gray-700'}`}
                    >
                      {imp.ragione_sociale}
                      <span className="text-xs text-gray-400 ml-2">{imp.citta} {imp.provincia ? `(${imp.provincia})` : ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selezione righe */}
            {nomeCedenteSel && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Seleziona righe da importare da "{nomeCedenteSel}"</label>
                  <div className="flex gap-3">
                    <button onClick={() => setRigheSelezionate(new Set(righeDisponibili.map((_, i) => i)))} className="text-xs text-[#2E6096] hover:underline">Tutte</button>
                    <button onClick={() => setRigheSelezionate(new Set())} className="text-xs text-gray-400 hover:underline">Nessuna</button>
                  </div>
                </div>
                {loadingImporta ? (
                  <p className="text-sm text-gray-400 py-4 text-center">Caricamento...</p>
                ) : righeDisponibili.length === 0 ? (
                  <p className="text-sm text-gray-400">Nessun dato proprio (non-cedente) trovato per questa impresa nella sezione corrente.</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                    {righeDisponibili.map((row, i) => (
                      <label key={i} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={righeSelezionate.has(i)}
                          onChange={() => toggleRiga(i)}
                          className="w-4 h-4 accent-[#1A3A5C]"
                        />
                        <span className="text-sm text-gray-700 font-medium w-20">{labelAnno(row)}</span>
                        {sezione === 'cifra_affari' && (
                          <span className="text-xs text-gray-400">
                            Affari: {fmt2(row.affari_totale)} · In lavori: {fmt2(row.affari_in_lavori)}
                          </span>
                        )}
                        {sezione === 'ammortamenti' && (
                          <span className="text-xs text-gray-400">
                            Beni diversi: {fmt2(row.amm_beni_diversi)} · Attrezzi: {fmt2(row.amm_attrezzi)}
                          </span>
                        )}
                        {sezione === 'costo_personale' && (
                          <span className="text-xs text-gray-400">
                            Operai: {fmt2(row.costo_operai)} · Tecnici: {fmt2(row.costo_tecnici)}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
                {sezione !== 'cifra_affari' && (
                  <p className="text-xs text-amber-600 mt-2">
                    Attenzione: le posizioni "Pos. N" del cedente si riferiscono alla cronologia della SUA scheda, non a
                    quella dell'impresa principale. Dopo l'importazione controlla/correggi il numero di posizione di ogni
                    riga (cliccandoci sopra) in modo che corrisponda allo stesso anno nella tabella "Cifra affari".
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-3">
            <button onClick={() => setModalCedente(false)} className="border border-gray-300 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Annulla</button>
            <button
              onClick={confermaImportaCedente}
              disabled={!nomeCedenteSel || righeSelezionate.size === 0}
              className="bg-[#1A3A5C] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition disabled:opacity-40"
            >
              Importa {righeSelezionate.size > 0 ? `(${righeSelezionate.size})` : ''}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
